import React, { useState, useEffect } from 'react';
import { UserCircle, Info } from "lucide-react";
import { FaRegCopy, FaCheck, FaRobot } from "react-icons/fa";
import { Tooltip } from "react-tooltip";
import TypingText from '../TypingText';
import { MessageListProps } from '../../types/types';

import { lightenColor } from "../../constants/lightenColor";
import { isLightColor } from "../../utils/LuminosityChecker";
import { darkenColor } from "../../constants/darkenColor";
import { getSafeImageSrc } from '../../utils/imageUtils';

declare global {
  interface Window {
    MathJax?: {
      typeset?: (elements?: Array<HTMLElement>) => void;
      typesetPromise?: (elements?: Array<HTMLElement>) => Promise<any>;
      startup?: {
        promise?: Promise<any>;
      };
      tex?: any;
      svg?: any;
      options?: any;
      [key: string]: any;
    }
  }
}

interface V1MessageListProps extends MessageListProps {
  themeOverride?: 'light' | 'dark' | 'auto';
}

const MessageList: React.FC<V1MessageListProps> = ({
  messages,
  configData,
  activeChatId,
  isNewChat,
  typingChatId,
  hasTyped,
  loading,
  isSidebarOpen,
  onScrollBottom,
  onChunkReference,
  messagesEndRef,
  loadingIndicatorRef,
  safeAiIconSrc,
  themeOverride = 'auto'
}) => {
  const [copiedMessage, setCopiedMessage] = useState<string | null>(null);
  const [activeMessageId, setActiveMessageId] = useState<string | null>(null);

  // Use configData colors as default with theme override
  const backgroundColor = configData.app_ui_settings.ui_backgroundcolor;
  const configIsLight = isLightColor(backgroundColor);
  
  // Determine effective theme based on themeOverride
  const effectiveIsLight = themeOverride === 'auto' ? configIsLight : themeOverride === 'light';
  
  // Theme-aware colors
  const lighterBackground = themeOverride === 'auto'
    ? (configIsLight ? darkenColor(backgroundColor, 5) : lightenColor(backgroundColor, 5))
    : (effectiveIsLight ? '#f7f7f8' : '#2d2d30');

  const darkerTabBackground = themeOverride === 'auto'
    ? (configIsLight ? darkenColor(backgroundColor, 15) : lightenColor(backgroundColor, 15))
    : (effectiveIsLight ? '#e5e5e5' : '#404040');

  const themeTextColor = themeOverride === 'auto'
    ? configData.app_ui_settings.ui_fontcolor
    : (effectiveIsLight ? '#000000' : '#ffffff');

  const iconColor = themeOverride === 'auto'
  ? configData.app_ui_settings.ui_iconcolor
  : effectiveIsLight
    ? 'black'
    : 'white';

  useEffect(() => {
  const updateSafeSource = async (): Promise<void> => {
    const baseSrc = configData.app_ai_settings.ai_face; // e.g., 'face.svg'

    let themeSuffix = '';
    if (themeOverride === 'auto') {
      themeSuffix = ''; // use default icon
    } else if (effectiveIsLight) {
      themeSuffix = '-light';
    } else {
      themeSuffix = '-dark';
    }

    // Compose new src, e.g. 'face-light.svg' or 'face-dark.svg'
    const srcWithTheme = baseSrc.replace(/(\.svg|\.png|\.jpg)$/, `${themeSuffix}$1`);

    const safeSrc = await getSafeImageSrc(srcWithTheme);
    // setSafeAiIconSrc(safeSrc);
  };
  updateSafeSource();
}, [configData.app_ai_settings.ai_face, themeOverride, effectiveIsLight]);

  // console.log("iconColor"+iconColor);

  function preserveDollarAmounts(text: string): string {
    return text.replace(/\$(\d+(?:\.\d+)?)/g, 'DOLLAR_PLACEHOLDER__$1__');
  }

  function restoreDollarAmounts(text: string): string {
    return text.replace(/DOLLAR_PLACEHOLDER__(\d+(?:\.\d+)?)__/g, (_, value) => `$${value}`);
  }

  function convertInlineMath(text: string): string {
    return text.replace(
      /(?<![\\$])\$(?!\d)((?:\\.|[^$\\])+?)(?<![\\])\$/g,
      (_, math) => `\\(${math.trim()}\\)`
    );
  }

  function formatText(text: string): string {
    let result = preserveDollarAmounts(text);
    result = convertInlineMath(result);
    result = result
      .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
      .replace(/###\s*(.*?)(?=\n|$)/g, "<h3>$1</h3>")
      .replace(/\n/g, "<br>")
      .replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" style="color: inherit; text-decoration: underline;">$1</a>')
      .replace(/(?<!\*)\*(?!\*)(.*?)\*(?!\*)/g, "<em>$1</em>");
    
    return restoreDollarAmounts(result);
  }

  const handleTypingFinished = (isTyping: boolean) => {
    setTimeout(() => {
      if (window.MathJax && window.MathJax.typesetPromise) {
        window.MathJax.typesetPromise()
          .catch((err: any) => console.error('MathJax error in parent:', err));
      } else if (window.MathJax?.typeset) {
        window.MathJax.typeset();
      }
    }, 0);
  };

  useEffect(() => {
    if (hasTyped && typeof window !== 'undefined' && window.MathJax?.typesetPromise) {
      window.MathJax.typesetPromise()
        .catch(err => console.error("MathJax render error:", err));
    }
  }, [hasTyped]);

  return (
    <div className="space-y-6">
      {messages.map((msg, index) => {
        const isLastAssistantMessage =
          index === messages.length - 1 && msg.sender === 'assistant';

        return (
          <div
            key={index}
            className="w-full"
          >
            {msg.sender === 'user' ? (
              // User message - Right aligned like ChatGPT
              <div className="flex justify-end">
                <div className="flex items-start gap-3 max-w-[70%]">
                  <div
                    className="group p-3 rounded-2xl"
                    style={{
                      backgroundColor: lighterBackground,
                      color: themeTextColor,
                      wordWrap: 'break-word',
                    }}
                    onClick={() => {
                      if (window.innerWidth <= 1024) {
                        setActiveMessageId(prev => (prev === msg.message_id ? null : msg.message_id!));
                      }
                    }}
                  >
                    <span style={{ display: 'inline' }}>
                      <span
                        dangerouslySetInnerHTML={{
                          __html: formatText(msg.message)
                        }}
                      />
                    </span>
                  </div>
                  <UserCircle className="h-6 w-6 mt-1 flex-shrink-0" style={{ color: themeTextColor }} />
                </div>
              </div>
            ) : (
              // Assistant message - Left aligned like ChatGPT
              <div className="flex justify-start">
                <div className="flex items-start gap-3 max-w-[70%]">
                  {safeAiIconSrc ? (
                    <img
                      src={safeAiIconSrc}
                      alt="AI Face"
                      className="h-6 w-6 flex-shrink-0 object-contain"
                      style={{
                        color: iconColor,
                        minWidth: '1.5rem',
                        minHeight: '1.5rem',
                      }}
                    />
                  ) : (
                    <FaRobot
                      className="h-6 w-6 mt-1 flex-shrink-0"
                      style={{ color: iconColor }}
                    />
                  )}
                  <div
                    className="group flex flex-col"
                    style={{
                      color: themeTextColor,
                      wordWrap: 'break-word',
                    }}
                    onClick={() => {
                      if (window.innerWidth <= 1024) {
                        setActiveMessageId(prev => (prev === msg.message_id ? null : msg.message_id!));
                      }
                    }}
                  >
                    <div className="prose prose-sm max-w-none">
                      {isLastAssistantMessage && isNewChat && typingChatId === activeChatId && !hasTyped ? (
                        <TypingText 
                          text={msg.message} 
                          speed={3} 
                          onTypingFinished={handleTypingFinished} 
                          onTypingUpdate={onScrollBottom}
                        />
                      ) : (
                        <span style={{ display: 'inline' }}>
                          <span
                            dangerouslySetInnerHTML={{
                              __html: formatText(msg.message)
                            }}
                          />
                        </span>
                      )}
                    </div>

                    <div className={`
                      flex justify-start mt-2 -ml-1
                      ${window.innerWidth <= 1024
                        ? activeMessageId === msg?.message_id
                          ? 'flex'
                          : 'hidden'
                        : 'hidden group-hover:flex'}
                    `}>
                      {copiedMessage === msg.message ? (
                        <span 
                          className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-opacity-80 transition-colors"
                          style={{ backgroundColor: 'transparent' }}
                          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = darkerTabBackground)}
                          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                        >
                          <FaCheck className="h-4 w-4" title="Copied" style={{ color: themeTextColor }} />
                        </span>
                      ) : (
                        <>
                          <span  
                            data-tooltip-id='copy_msg'
                            className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-opacity-80 transition-colors cursor-pointer"
                            style={{ backgroundColor: 'transparent' }}
                            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = darkerTabBackground)}
                            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                          >
                            <FaRegCopy
                              className="h-4 w-4"
                              style={{ color: themeTextColor }}
                              onClick={() => {
                                const plainText = msg.message.replace(/<[^>]+>/g, '');
                                navigator.clipboard.writeText(plainText);
                                setCopiedMessage(msg.message);
                                setTimeout(() => setCopiedMessage(null), 2000);
                              }}
                            />
                          </span>
                          <Tooltip 
                            id='copy_msg' 
                            place="bottom" 
                            style={{ 
                              color: themeTextColor,
                              backgroundColor: lighterBackground
                            }}
                          >
                            Copy
                          </Tooltip>
                        </>
                      )}
                      {msg.isRefAvailable && (
                        <>
                          <span  
                            data-tooltip-id='reference_chunk'
                            className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-opacity-80 transition-colors cursor-pointer"
                            style={{ backgroundColor: 'transparent' }}
                            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = darkerTabBackground)}
                            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                          >
                            <Info
                              className="h-4 w-4"
                              style={{ color: themeTextColor }}
                              onClick={() => {
                                if (msg?.message_id) {
                                  onChunkReference(msg.message_id);
                                }
                              }}
                            />
                          </span>
                          <Tooltip 
                            id='reference_chunk' 
                            place="bottom" 
                            style={{ 
                              color: themeTextColor,
                              backgroundColor: lighterBackground
                            }}
                          >
                            Reference
                          </Tooltip>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}

      <div ref={messagesEndRef} />

      {loading && (
        <div className="flex justify-start">
          <div className="flex items-start gap-3">
            {safeAiIconSrc ? (
              <img
                src={safeAiIconSrc}
                alt="AI Face"
                className="h-6 w-6 mt-1 flex-shrink-0 object-contain"
                style={{ color: configData.app_ui_settings.ui_iconcolor }}
              />
            ) : (
<FaRobot
                className="h-6 w-6 mt-1 flex-shrink-0"
                style={{ color: themeTextColor }}
              />
            )}
            <div 
              ref={loadingIndicatorRef} 
              className="flex items-center text-sm font-medium"
              style={{ color: themeTextColor }}
            >
              Thinking<span className="dot-typing"></span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MessageList;